import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './ConversationComposer.sass'
import Icon from 'components/Icon'
import TextareaAutosize from 'react-textarea-autosize'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownDivider,
} from 'components/Dropdown'
import OneLine from 'components/OneLine'
import { IconButton } from 'components/IconButton'
import JobViewModel from 'jobs/views/JobRoot/Job/JobViewModel'
import ConversationViewModel from '../ConversationViewModel'
import Tooltip from 'components/Tooltip'
import FileButton from 'components/FileButton'
import { useToggle } from 'hooks/state-hooks'

interface ConversationComposerProps {
  vm: JobViewModel
  conversationVM: ConversationViewModel
  showDocumentTab(): void
  small?: boolean
}

export default observer(function ConversationComposer({
  vm,
  conversationVM,
  showDocumentTab,
  small,
}: ConversationComposerProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)

  const isNoteModeEnabled = conversationVM.noteMode.isEnabled

  const documentTypesForUser = vm.documentsVM.documentTypesForCollaboratorType

  const dropdownOpened = useToggle(false)
  const toggle = dropdownOpened.toggle

  return (
    <div className={styles.root}>
      <div
        className={cx(styles.composer, isNoteModeEnabled && styles.noteMode)}
      >
        {vm.canUploadDocs && (
          <div className={styles.addContainer}>
            <Dropdown
              position="top-start"
              isOpen={dropdownOpened.isEnabled}
              onRequestClose={dropdownOpened.disable}
              button={
                <a
                  onClick={toggle}
                  className={cx(
                    styles.addButton,
                    dropdownOpened.isEnabled && styles.dropdownOpen
                  )}
                >
                  <Icon name="add" />
                </a>
              }
              dataTest="chat-add-document-dropdown-new"
            >
              <DropdownTitle>Add new</DropdownTitle>
              {documentTypesForUser.map((type) => (
                <DropdownItem
                  onClick={() => showDocumentTab()}
                  key={type.id}
                  relative
                >
                  <OneLine>
                    <FileButton
                      overlay
                      onChange={(files: FileList) => {
                        vm.documentsVM.stageFileAsType(files, type)
                        toggle()
                      }}
                      data-test={`chat-upload-document-input-${type.name}`}
                    />
                    {type.name}
                  </OneLine>
                </DropdownItem>
              ))}
              <DropdownItem onClick={() => showDocumentTab()} relative>
                <OneLine>
                  <FileButton
                    overlay
                    multiple={true}
                    onChange={(files: FileList) => {
                      vm.documentsVM.stageFiles(files)
                      toggle()
                    }}
                    data-test="chat-upload-document-input-new"
                  />
                  Other documents
                </OneLine>
              </DropdownItem>
              <DropdownDivider />
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                onClick={() => {
                  vm.documentLinkingPickerVM.show()
                  toggle()
                }}
                data-test="chat-add-file-link-existing-dropdown-item-new"
              >
                <OneLine>Link existing</OneLine>
              </DropdownItem>

              {conversationVM.shouldShowMessageBank && (
                <DropdownItem onClick={() => vm.messageBankDialogVM.show()}>
                  <OneLine>Message bank reply</OneLine>
                </DropdownItem>
              )}
            </Dropdown>
          </div>
        )}
        <div
          className={styles.inputContainer}
          onClick={() => textareaRef.current && textareaRef.current.focus()}
        >
          <TextareaAutosize
            className={cx(
              styles.input,
              conversationVM.composerText && styles.filled
            )}
            ref={textareaRef}
            data-private
            data-test="conversation-input"
            // Have to use style here because the library adds it itself.
            style={{ resize: 'none' }}
            placeholder={`${
              isNoteModeEnabled
                ? 'Write your note here'
                : 'Write your message here'
            }.${small ? '' : ' Shift + Enter to add a new line.'}`}
            value={conversationVM.composerText}
            maxRows={20}
            onChange={(e) =>
              conversationVM.setComposerText(
                (e.target as HTMLTextAreaElement).value
              )
            }
            onKeyPress={(e) => {
              if (e.charCode === 13 && !e.shiftKey) {
                e.preventDefault()
                conversationVM.sendMessage()
              }
            }}
          />
        </div>
        <div className={styles.extrasContainer}>
          <Tooltip
            placement="top"
            content={
              isNoteModeEnabled
                ? 'Click to switch to Message'
                : 'Click to switch to Note'
            }
          >
            <IconButton
              variant="ghost"
              onClick={conversationVM.noteMode.toggle}
            >
              {/* TODO(jeffijoe): this is temporary until we have a design */}
              <Icon
                name="chat-note"
                size={45}
                color={isNoteModeEnabled ? 'var(--purple700)' : undefined}
              />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  )
})
